<template>
  <div>
    <template v-if="showControl.editable">
      <div class="mt-[20px] mb-2">
        <BaseElButton data-cy="add-period-btn" plain @click="addTimes">{{ displayText.title }}</BaseElButton>
      </div>
      <BaseElFormItem prop="customWeekConfigs" label-width="0">
        <div
          v-for="(item, index) in data"
          :key="index + item.timeStart"
          class="flex align-items-stretch mb-2"
        >
          <BaseElSelect
            v-if="showControl.isWeek"
            v-model="item.week"
            :placeholder="displayText.weekPlaceholder"
          >
            <BaseElSelectOption
              v-for="item in weeksOptions"
              :key="item.value"
              :label="$t(item.label)"
              :value="item.value"
            />
          </BaseElSelect>
          <el-time-select
            v-model="item.timeStart"
            data-cy="start-time-select"
            class="w-full ml-2"
            :placeholder="displayText.timeStartPlaceholder"
            :picker-options="{
              start: '00:00',
              step: timeStep,
              end: '24:00',
            }"
          />
          <el-time-select
            v-model="item.timeEnd"
            data-cy="end-time-select"
            class="w-full ml-2"
            :placeholder="displayText.timeEndPlaceholder"
            :picker-options="{
              start: item.timeStart,
              step: timeStep,
              end: '24:00',
              minTime: item.timeStart,
            }"
          />
          <div
            class="close p-2 ml-2 cursor-pointer"
            @click="remove(index)"
          >
            <SVGIcon
              icon="common-trash"
              fill="var(--gray-60)"
            />
          </div>
        </div>
      </BaseElFormItem>
    </template>
    <div class="business-time-grid" :class="{ editable: showControl.editable }">
      <div class="info">
        <div class="flex items-center gap-[10px]">
          <div class="block" />
          <p>{{ displayText.closedPeriod }}</p>
        </div>
        <div class="flex items-center gap-[10px]">
          <div class="block active" />
          <p>{{ displayText.openPeriod }}</p>
        </div>
      </div>
      <div class="flex flex-col">
        <!-- Header Row -->
        <div class="grid gap-[4px] grid-flow-col">
          <div class="hour-labels">
            <span class="hour-label">0</span>
            <span class="hour-label">6</span>
            <span class="hour-label">12</span>
            <span class="hour-label">18</span>
            <span class="hour-label">24</span>
          </div>
        </div>
        <!-- Day Rows -->
        <div v-for="(day, dayIndex) in weekList" :key="dayIndex" class="day-row">
          <div class="week">{{ day }}</div>
          <div v-for="hour in 24" :key="hour" class="block" :class="isBusinessHour(dayIndex, hour)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'
import { weeksOptions } from '@/config/date'
import { find } from 'lodash'
import { i18n } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'AddTimes',
  props: {
    title: {
      type: String,
      default: '新增營業時間',
    },
    timeStep: String,
    isWeek: Boolean,
    customWeekConfigs: Array,
    editable: {
      type: Boolean,
      default: true,
    },
  },
  emits: [],
  setup (props, { emit }) {
    const data = computed(() => props.customWeekConfigs)
    const weekList = ['日', '一', '二', '三', '四', '五', '六']
    const emptyTime = { week: null, timeStart: '', timeEnd: '' }
    const remove = (index) => {
      data.value.splice(index, 1)
    }
    const addTimes = () => {
      const times = data.value
      times.push(JSON.parse(JSON.stringify(emptyTime)))
    }

    const isBusinessHour = (dayIndex, hour) => {
      const config = find(data.value, ({ week, timeStart, timeEnd }) => {
        const [startHour, startMinute] = timeStart.split(':').map(Number)
        const [endHour, endMinute] = timeEnd.split(':').map(Number)

        return (
          week === dayIndex &&
          (startHour <= hour - 1 || (startHour === hour - 1 && startMinute === 0)) &&
          (endHour >= hour - 1 || (endHour === hour - 1 && endMinute === 0))
        )
      })

      if (!config) return ''

      const [startHour, startMinute] = config.timeStart.split(':').map(Number)
      const [endHour, endMinute] = config.timeEnd.split(':').map(Number)

      if (startHour === hour - 1 && startMinute > 0) return 'half-active-end'
      if (endHour === hour - 1 && endMinute > 0) return 'half-active-start'
      if (endHour === hour - 1 && endMinute === 0) return ''
      return 'active'
    }

    const showControl = computed(() => {
      const controls = {
        editable: props.editable,
        isWeek: props.isWeek,
      }
      return controls
    })

    const displayText = computed(() => {
      const data = {
        title: props.title,
        weekPlaceholder: i18n.t('businessTimeSettings.week.placeholder'),
        timeStartPlaceholder: i18n.t('businessTimeSettings.timeStart.placeholder'),
        timeEndPlaceholder: i18n.t('businessTimeSettings.timeEnd.placeholder'),
        closedPeriod: '關閉時段',
        openPeriod: '開放時段',
      }
      return data
    })

    return { data, addTimes, remove, weeksOptions, isBusinessHour, weekList, showControl, displayText }
  },
})
</script>

<style lang="postcss" scoped>
.business-time-grid {
  @apply grid mt-[20px];
  @apply w-full;
  &.editable {
    @apply w-[calc(100%-44px)]
  }
}

.business-time-grid .block {
  @apply w-[16px] h-[16px] rounded-[2px] bg-gray-20;
  &.active {
    @apply bg-success
  }
  &.half-active-start {
    @apply bg-success;
    background: linear-gradient(to right, var(--success) 50%, var(--gray-20) 50%);
  }
  &.half-active-end {
    @apply bg-success;
    background: linear-gradient(to left, var(--success) 50%, var(--gray-20) 50%);
  }
}
.day-row {
  @apply grid gap-[4px] grid-flow-col;

  & > :nth-child(6n+7) {
    margin-right: 12px;
  }

  & > :last-child {
    margin-right: 0;
  }
}
.empty-block {
  @apply w-[64px]
}
.hour-labels {
  @apply flex justify-evenly ml-[68px] mb-[6px] relative
}
.hour-label {
  @apply text-[6px] w-[12px] text-center;
  &:first-child {
    @apply absolute left-0 text-left;
  }
  &:last-child {
    @apply absolute right-0 text-right;
  }
}
.week {
  @apply text-center px-[18px] mr-[13px]
}
.info {
  @apply flex flex-row-reverse gap-[12px] mb-[12px]
}
</style>
